import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { CancelSubmitButtons } from "~/common/components/ui";
import Input from "~/common/components/ui/Input";
import { INTEGRATION_TYPE } from "~/common/constants";
import { integrationUpsertSchema } from "~/common/schemas/integration";
import { customErrorSchema } from "~/common/types";
import { Integration, IntegrationUpsert } from "~/common/types/integration";
import { useCurrentTeamMemberContext } from "~/providers/hooks/useCurrentTeamMemberContext";
import { useUpsertIntegration } from "~/providers/hooks/useUpsertIntegration";

export const IntegrationForm = ({
  integration,
  onCancel,
  onError,
  onSuccess,
}: {
  integration: Integration;
  onCancel?: () => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}) => {
  const {
    organization_team_members: [organization_team_member],
  } = useCurrentTeamMemberContext();
  const { mutate, isPending } = useUpsertIntegration();

  const formType =
    integration.type === INTEGRATION_TYPE.ZOOM
      ? INTEGRATION_TYPE.ZOOM
      : integration.type === INTEGRATION_TYPE.SPRUCE
        ? INTEGRATION_TYPE.SPRUCE
        : INTEGRATION_TYPE.STRIPE;

  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
  } = useForm<IntegrationUpsert>({
    resolver: zodResolver(integrationUpsertSchema),
    mode: "onTouched",
    defaultValues: {
      id: integration.id ?? undefined,
      type: formType,
    },
  });

  const submitHandler: SubmitHandler<IntegrationUpsert> = (data) => {
    mutate(
      {
        integration: data,
      },
      {
        onError: (error) => {
          const result = customErrorSchema.safeParse(error);

          toast.error(
            result.success
              ? result.data.response.data.detail
              : "There was an issue saving your integration options. Please try again later.",
          );
          onError?.(error);
        },
        onSuccess,
      },
    );

    return data;
  };

  if (
    integration.type !== INTEGRATION_TYPE.STRIPE &&
    integration.type !== INTEGRATION_TYPE.ZOOM &&
    integration.type !== INTEGRATION_TYPE.SPRUCE
  ) {
    return null;
  }

  return (
    <form noValidate onSubmit={handleSubmit(submitHandler)}>
      {integration.type === INTEGRATION_TYPE.STRIPE && (
        <>
          <input
            type="hidden"
            {...register("organization_id")}
            value={organization_team_member?.organization_id}
          />
          <fieldset>
            <legend className="hidden">Stripe integration configuration</legend>
            <div className="mb-4">
              <label
                className="mb-1 block font-medium"
                htmlFor="input-settings-publishable-key"
              >
                Stripe publishable key
              </label>
              <Input
                error={
                  "publishable_key" in errors
                    ? errors.publishable_key?.message
                    : undefined
                }
                id="input-settings-publishable-key"
                placeholder="e.g. pk_live_..."
                type="text"
                {...register("publishable_key")}
              />
            </div>
            <div className="mb-4">
              <label
                className="mb-1 block font-medium"
                htmlFor="input-settings-restricted-api-key"
              >
                Stripe restricted API key
              </label>
              <Input
                error={
                  "restricted_api_key" in errors
                    ? errors.restricted_api_key?.message
                    : undefined
                }
                id="input-settings-restricted-api-key"
                placeholder="e.g. rk_live_..."
                type="text"
                {...register("restricted_api_key")}
              />
            </div>
          </fieldset>
        </>
      )}

      {integration.type === INTEGRATION_TYPE.ZOOM && (
        <>
          <input
            type="hidden"
            {...register("organization_id")}
            value={organization_team_member?.organization_id}
          />
          <fieldset>
            <legend className="hidden">Zoom integration configuration</legend>
            <div className="mb-4">
              <label
                className="mb-1 block font-medium"
                htmlFor="input-settings-zoom-account-id"
              >
                Zoom account ID
              </label>
              <Input
                error={
                  "account_id" in errors
                    ? errors.account_id?.message
                    : undefined
                }
                id="input-settings-zoom-account-id"
                placeholder="e.g. abc123..."
                type="text"
                {...register("account_id")}
              />
            </div>
            <div className="mb-4">
              <label
                className="mb-1 block font-medium"
                htmlFor="input-settings-zoom-client-id"
              >
                Zoom client ID
              </label>
              <Input
                error={
                  "client_id" in errors ? errors.client_id?.message : undefined
                }
                id="input-settings-zoom-client-id"
                placeholder="e.g. xyz789..."
                type="text"
                {...register("client_id")}
              />
            </div>
            <div className="mb-4">
              <label
                className="mb-1 block font-medium"
                htmlFor="input-settings-zoom-client-secret"
              >
                Zoom client secret
              </label>
              <Input
                error={
                  "client_secret" in errors
                    ? errors.client_secret?.message
                    : undefined
                }
                id="input-settings-zoom-client-secret"
                placeholder="e.g. secret..."
                type="password"
                {...register("client_secret")}
              />
            </div>
          </fieldset>
        </>
      )}

      {integration.type === INTEGRATION_TYPE.SPRUCE && (
        <>
          <input
            type="hidden"
            {...register("organization_id")}
            value={organization_team_member?.organization_id}
          />
          <fieldset>
            <legend className="hidden">Spruce integration configuration</legend>
            <div className="mb-4">
              <label
                className="mb-1 block font-medium"
                htmlFor="input-settings-spruce-token"
              >
                Spruce API token
              </label>
              <Input
                error={"token" in errors ? errors.token?.message : undefined}
                id="input-settings-spruce-token"
                placeholder="Enter your Spruce API token"
                type="password"
                {...register("token")}
              />
            </div>
          </fieldset>
        </>
      )}

      <div className="pt-4">
        <CancelSubmitButtons
          submitButtonLabel="Connect"
          cancelClassName="flex-1"
          submitClassName="flex-1"
          handleCancel={onCancel}
          isSubmitting={isPending}
          containerClassName="w-1/2 ml-auto"
          disableSubmit={!isDirty}
        />
      </div>
    </form>
  );
};
